import React, { ReactElement } from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { ApprovalStatus, Absence, AbsenceType, Country } from "../../API";
import { Tooltip } from "../../components";
import { getBusinessDayDifference, parseDate } from "../../lib";

type Actions = {
  showUpdateModal: () => void;
  showDeleteModal: () => void;
  remaining: number;
  absences: Absence[];
  employeeID: string;
  maxDate: Date;
  minDate: Date;
  country: Country;
  vacationAllowance?: number | null;
};

type RowAction = {
  label: string;
  icon: ReactElement;
  onClick: (
    params: GridRenderCellParams & {
      maxAmount?: number;
      absences?: Absence[];
      employeeID?: string;
      country?: Country;
      maxDate?: Date;
      minDate?: Date;
    }
  ) => void;
};

const displayStatus = (sts: string) => sts[0].toUpperCase() + sts.slice(1);

const disabledAction = (status: string) => status !== ApprovalStatus.pending;

const renderActionButton = (
  { icon, label, onClick }: RowAction,
  params: GridRenderCellParams,

  remaining?: number,
  absences?: Absence[],
  employeeID?: string,
  country?: Country,
  maxDate?: Date,
  minDate?: Date
) =>
  disabledAction(params.row.approvalStatus) ? (
    <></>
  ) : (
    <Tooltip title={label} placement="top">
      <div data-testid={`grid-${label.toLowerCase()}-button`}>
        <GridActionsCellItem
          label={label}
          onClick={() => {
            onClick({
              ...params,
              maxAmount: remaining,
              absences,
              employeeID,
              country,
              maxDate,
              minDate,
            });
          }}
          icon={icon}
          {...({} as any)}
        />
      </div>
    </Tooltip>
  );

export const columns = ({
  showUpdateModal,
  showDeleteModal,
  remaining,
  absences,
  vacationAllowance,
  employeeID,
  country,
  maxDate,
  minDate,
}: Actions): GridColDef<Absence>[] => [
  {
    field: "startDate",
    headerName: "Start",
    minWidth: 112,
    renderCell: ({ value }) => format(parseDate(value), "dd/MM/yyyy"),
  },
  {
    field: "endDate",
    headerName: "End",
    minWidth: 112,
    renderCell: ({ value }) => format(parseDate(value), "dd/MM/yyyy"),
  },
  {
    field: "hoursTaken",
    headerName: "Hours",
  },
  {
    field: "amount",
    headerName: "Amount",
    align: "left",
    valueGetter: ({ row }) => {
      if (row.type !== AbsenceType.holiday) {
        return getBusinessDayDifference(
          new Date(row.startDate),
          new Date(row.endDate)
        );
      }

      return row.amount;
    },
  },
  {
    field: "type",
    headerName: "Type",
    align: "left",
  },
  {
    field: "remaining",
    headerName: "Remaining days",
    align: "left",
    valueGetter: ({ row }) => {
      const allowance = vacationAllowance;

      if (!allowance) return 0;
      if (row.type !== AbsenceType.holiday) return "";
      // support either row.employee.id or row.employeeID
      const employeeId = row.employeeID;

      // parse current row start date to compute cumulative usage up to this row
      const rowStart = row.startDate ? parseISO(row.startDate) : null;

      const used = (absences ?? [])
        .filter((usedAbsence) => {
          const aEmployeeId = usedAbsence?.employeeID;
          // only same employee (or all if employeeId is missing)
          if (employeeId != null && aEmployeeId !== employeeId) return false;

          // only count approved or pending
          const isCounted = (
            [ApprovalStatus.approved, ApprovalStatus.pending] as string[]
          ).includes(usedAbsence?.approvalStatus ?? "");
          if (!isCounted) return false;

          // if we have dates, only include absences that start on or before this row's start
          if (!rowStart || !usedAbsence?.startDate) return true;
          try {
            // include if usedAbsence.startDate <= row.startDate
            return parseISO(usedAbsence.startDate) <= rowStart;
          } catch {
            return true;
          }
        })
        .reduce((sum, usedAbsence) => sum + (usedAbsence?.amount ?? 0), 0);

      return Math.max(allowance - used, 0);
    },
  },
  {
    field: "approvalStatus",
    headerName: "Approval status",
    width: 128,
    renderCell: ({ value }) => displayStatus(value ?? "pending"),
  },
  {
    field: "description",
    headerName: "Applicant Description",
    flex: 1,
    renderCell: ({ value }) => value,
  },
  {
    field: "notes",
    headerName: "Approver notes",
    flex: 1,
    renderCell: ({ value }) => value,
  },
  {
    field: "actions",
    headerName: "Actions",
    cellClassName: "actions",
    width: 96,
    sortable: false,
    headerAlign: "left",
    renderCell: (params) => (
      <>
        {renderActionButton(
          {
            label: "Edit",
            icon: <EditIcon />,
            onClick: showUpdateModal,
          },
          params,
          remaining,
          absences,
          employeeID,
          country,
          maxDate,
          minDate
        )}
        {renderActionButton(
          {
            label: "Delete",
            icon: <DeleteIcon />,
            onClick: showDeleteModal,
          },
          params
        )}
      </>
    ),
  },
];

export const hiddenColumnsMobile: { [key: string]: boolean } = {
  endDate: false,
  amount: false,
  approver: false,
  description: true,
  actions: false,
};
